import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/articles";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  const goTo = useCallback(
    (index: number) => setActive((index + total) % total),
    [total],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [total]);

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLElement>) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta > 0 ? active - 1 : active + 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden bg-primary-950 md:h-[680px] lg:h-[720px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点内容轮播"
    >
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 台北娜娜`}
            className={`h-full w-full object-cover object-top transition-transform duration-[6000ms] ${
              index === active ? "scale-105" : "scale-100"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/40 to-primary-950/50" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full flex-col justify-end px-4 pb-24 md:px-8 md:pb-28 lg:px-12">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-4 py-1.5 text-xs font-medium text-background-50">
            <i className="ri-flashlight-fill" />
            {heroSlides[active].tag}
          </span>
          <h2
            key={heroSlides[active].id}
            className="mt-5 font-heading text-3xl font-extrabold leading-tight text-background-50 animate-fade-up md:text-5xl lg:text-[3.25rem]"
          >
            {heroSlides[active].title}
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-50/80 md:text-base">
            {heroSlides[active].description}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${heroSlides[active].id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              了解品牌
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-4 z-10 flex items-center gap-3 md:right-8 lg:right-12">
        <button
          type="button"
          onClick={() => goTo(active - 1)}
          aria-label="上一张"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
        >
          <i className="ri-arrow-left-line" />
        </button>
        <button
          type="button"
          onClick={() => goTo(active + 1)}
          aria-label="下一张"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
        >
          <i className="ri-arrow-right-line" />
        </button>
      </div>

      <div className="absolute bottom-10 left-4 z-10 flex items-center gap-2 md:left-8 lg:left-12">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            aria-label={`切换到第 ${index + 1} 张`}
            className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
              index === active ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}